import {
  AttributeVariant,
  ConfigurableVariant, MagentoConfigurableProductOptions,
  MagentoConfigurableProductOptionsValues,
  MagentoProductPrices, MagentoSku,
  ProductData
} from 'lib/types'
import { VARIANT, ZERO } from 'lib/core/resources/constants'
import isEqual from 'lodash/isEqual'
import isMatch from 'lodash/isMatch'
import filter from 'lodash/filter'

const STATUS_DISABLED = '2' // Custom Magento core bug fix

export interface ConfigurableOptionsType {
  isDisabled?: boolean
  attrCode: string
  attrId: string
  id: number
  label: string
  productId: number
  position: number
  values: Option[]
}

export interface Option {
  id: number
  label: string
  value: number
  swatch: string
  priceDiff: PriceDiff | undefined
  isDisabled: boolean
  sortWeight: number | undefined
}

export interface OptionsIndex {
  [key: string]: Option | undefined
}

export interface PriceDiff {
  currency: string
  value: number
}

export interface ConfigurableFilters {
  [key: string]: number
}

export interface GetConfigurableOptionsParams {
  product?: ProductData
  currentValue: OptionsIndex | undefined
  basePrice: MagentoProductPrices
  reloadOptions?: boolean
}

export const getConfigurableOptions = ({
  product,
  currentValue,
  basePrice,
  reloadOptions
}: GetConfigurableOptionsParams): ConfigurableOptionsType[] => {
  if (!product?.configurableOptions?.length) {
    return []
  }

  const attrVariants = getAttributeVariants(product)
  const filters = getFilters(currentValue)
  const optionsCount = Object.keys(currentValue ?? {}).length
  const hasVariants = Boolean(attrVariants.length)
  return product.configurableOptions.map(option => {
    const {
      attribute_id: attrId,
      attribute_code: attrCode,
      id,
      label,
      product_id: productId,
      values,
      position
    } = option

    const optionValues = getOptionValues({
      values,
      filters,
      attrCode,
      optionsCount,
      attrVariants,
      hasVariants,
      configurableOptions: product.configurableOptions ?? [],
      reloadOptions,
      basePrice
    })

    return {
      attrCode,
      attrId,
      id,
      label,
      productId,
      position,
      values: optionValues
    }
  })
}

export const getVariants = (product?: ProductData): ConfigurableVariant[] => {
  if (!product) {
    return []
  }

  const products = product.variants.filter(variant => variant.product) // Magento sends nullish products :)
  return products ?? []
}

export const getVariantDefault = (product?: ProductData, sku?: MagentoSku): ConfigurableVariant | undefined => {
  if (!product) {
    return undefined
  }

  const defaultVariant = sku ? getVariants(product).find(variant => variant?.product.sku === sku) : undefined
  return defaultVariant ?? getVariants(product).find(variant => variant?.product.isDefault)
}

export const getAttributeVariants = (product?: ProductData): AttributeVariant[] => {
  const variants = getVariants(product)
  const _attrVariants: AttributeVariant[] = []
  let i = 0

  for (const variant of variants) {
    const _attributes = variant.attributes
    const _attrVariant: AttributeVariant = {
      ...variant,
      attrIndex: {}
    }

    for (const attr of _attributes) {
      _attrVariant.attrIndex[attr.code] = attr.value_index
    }

    _attrVariants[i] = _attrVariant
    i++
  }

  return _attrVariants
}

export interface MakeOptionValues {
  regularOption: MagentoConfigurableProductOptionsValues
  filters: ConfigurableFilters
  attrCode: string
  optionsCount: number
  attrVariants: AttributeVariant[]
  hasVariants: boolean
  configurableOptions: MagentoConfigurableProductOptions[]
  reloadOptions?: boolean
  basePrice: MagentoProductPrices
}

const makeOption = ({
  regularOption,
  filters,
  attrCode,
  optionsCount,
  attrVariants,
  hasVariants,
  configurableOptions,
  reloadOptions,
  basePrice
}: MakeOptionValues): Option => {
  const {
    label,
    value_index: value,
    visiture_swatch_data: swatchData
  } = regularOption

  const swatch = swatchData?.value
  const options = { ...filters, [attrCode]: value }
  const optionsReloaded = { ...options }
  if (reloadOptions) {
    const optionIndex = configurableOptions.findIndex(el => el.attribute_code === attrCode)
    const removeAttrCodes: string[] = []
    configurableOptions.forEach((el, i) => {
      if (i > optionIndex) {
        removeAttrCodes.push(el.attribute_code)
      }
    })
    removeAttrCodes.forEach(attrCode => {
      delete optionsReloaded[attrCode]
    })
  }

  const areAllOptionsSelected = Object.keys(options).length === optionsCount
  const variantsWithStock = findVariantsWithStock(attrVariants, reloadOptions ? optionsReloaded : options)
  const variant = areAllOptionsSelected ? variantsWithStock[0] : undefined
  const optionHasVariantsWithStock = Boolean(variantsWithStock.length)
  const priceDiff = variant
    ? getPriceDiff(basePrice, variant.product.price)
    : getPriceDiff(basePrice, findLowestPrice(attrVariants, options))

  const isDisabled = !optionHasVariantsWithStock
  const sortWeight = priceDiff?.value

  const option: Option = {
    id: value,
    label,
    value,
    swatch,
    priceDiff,
    isDisabled,
    sortWeight
  }

  return option
}

/**
 * @param attrVariants
 * @param filters
 */
export const findVariant = (attrVariants: AttributeVariant[], filters: ConfigurableFilters) => {
  const variant = attrVariants.find(variant => {
    const isEnabled = variant?.product.status !== STATUS_DISABLED // Ugly trickery, thanks Magento
    const inStock = variant.product && variant.product.stockStatus === VARIANT.IN_STOCK
    return isEnabled && inStock && isEqual(variant.attrIndex, filters)
  })

  return variant
}

/**
 * @param attrVariants
 * @param filters
 */
export const findVariantsWithStock = (attrVariants: AttributeVariant[], filters: ConfigurableFilters) => {
  const variants = attrVariants.filter(variant => {
    const isEnabled = variant?.product.status !== STATUS_DISABLED // Ugly trickery, thanks Magento
    const inStock = variant.product && variant.product.stockStatus === VARIANT.IN_STOCK
    return isEnabled && inStock && isMatch(variant.attrIndex, filters)
  })

  return variants
}

/**
 * @param attrVariants
 * @param filters
 */
export const filterVariants = (attrVariants: AttributeVariant[], filters: ConfigurableFilters) => {
  return filter(attrVariants, {
    attrIndex: filters
  })
}

/**
 * @param basePrice
 * @param newPrice
 */
const getPriceDiff = (basePrice: MagentoProductPrices, newPrice?: MagentoProductPrices) => {
  const basePriceValue = basePrice?.regularPrice?.amount.value
  const newPriceValue = newPrice?.regularPrice?.amount.value
  const currency = newPrice ? newPrice.regularPrice.amount.currency : basePrice.regularPrice.amount.currency
  const noDiff = {
    currency,
    value: ZERO
  }

  if (!newPriceValue || !basePriceValue) {
    return noDiff
  }

  if (newPriceValue !== basePriceValue) {
    const isNegative = newPriceValue < basePriceValue
    const amount = Math.abs(newPriceValue - basePriceValue) * (isNegative ? -1 : 1)
    return {
      currency,
      value: amount
    }
  } else {
    return noDiff
  }
}

/**
 * @param attrVariants
 * @param filters
 */
export const findLowestPrice = (attrVariants: AttributeVariant[], filters: ConfigurableFilters) => {
  let lowestPrice: number
  let priceObject: MagentoProductPrices | undefined
  const variants = filterVariants(attrVariants, filters)

  variants.forEach(variant => {
    const { product } = variant
    if (!product) {
      return
    }

    const { price } = product
    const { regularPrice } = price
    const { amount } = regularPrice
    const { value } = amount

    if (lowestPrice == null || value < lowestPrice) {
      lowestPrice = value
      priceObject = price
    }
  })

  return priceObject
}

export interface OptionValues {
  values: MagentoConfigurableProductOptionsValues[]
  filters: ConfigurableFilters
  attrCode: string
  optionsCount: number
  attrVariants: AttributeVariant[]
  hasVariants: boolean
  configurableOptions: MagentoConfigurableProductOptions[]
  reloadOptions?: boolean
  basePrice: MagentoProductPrices
}

export const getOptionValues = ({
  values,
  filters,
  attrCode,
  optionsCount,
  attrVariants,
  hasVariants,
  configurableOptions,
  reloadOptions,
  basePrice
}: OptionValues): Option[] => {
  return values.map(item => {
    return makeOption({
      regularOption: item,
      filters,
      attrCode,
      optionsCount,
      attrVariants,
      hasVariants,
      configurableOptions,
      reloadOptions,
      basePrice
    })
  })
}

/**
 * @param optionsIndex
 */
export const getFilters = (optionsIndex: OptionsIndex | undefined): ConfigurableFilters => {
  const filters: ConfigurableFilters = {}

  if (optionsIndex) {
    for (const attrCode in optionsIndex) {
      const option = optionsIndex[attrCode]
      if (option) {
        filters[attrCode] = option.value
      }
    }
  }

  return filters ?? {}
}
